/**
 * External dependencies
 */
import type { Uuid } from 'uuid';
import type {
	ExperimentId,
	Maybe,
	TrackEvent,
	UniqueVisitEvent,
	VisitEvent,
} from '@nab/types';

/**
 * Internal dependencies
 */
import { getCookie, removeCookie, setCookie } from './cookies';
import {
	areHeatmapConditionsValid,
	doesExperimentHaveValidSegments,
} from './segmentation';
import { pick } from '../utils/lodash';
import type { GoalIndex, HeatmapSummary, Session } from '../types';

type ExpsWithViews = Partial< Record< ExperimentId, Timestamp > >;
type Timestamp = number;

const TEN_DAYS_IN_MILLIS = 14400 * 60000;

export function updateExperimentsWithPageViews(
	events: ReadonlyArray< VisitEvent >,
	session: Session
): void {
	removeOldExperimentsFromCookie( session );
	if ( ! events.length ) {
		return;
	}

	const now = new Date().getTime();
	setExperimentsWithPageViewsCookie( {
		...getExperimentsWithPageViews(),
		...events.reduce( ( result, event ) => {
			result[ event.experiment ] = now;
			return result;
		}, {} as ExpsWithViews ),
	} );
}

export function getUniqueViews(): Partial< Record< ExperimentId, Uuid > > {
	try {
		const value = getCookie( 'nabUniqueViews' ) ?? '';
		return JSON.parse( value ) as Partial< Record< ExperimentId, Uuid > >;
	} catch ( _ ) {
		return {};
	}
}

export function updateUniqueViews(
	value: ReadonlyArray< UniqueVisitEvent >
): void {
	const allUniqueViews = {
		...getUniqueViews(),
		...value.reduce(
			( r, v ) => ( { ...r, [ v.experiment ]: v.id } ),
			{} as Record< ExperimentId, Uuid >
		),
	};

	const views = getExperimentsWithPageViews();
	const uniqueViews = pick(
		allUniqueViews,
		Object.keys( views ).map( ( k ) =>
			Number.parseInt( k )
		) as ReadonlyArray< ExperimentId >
	);
	if ( 0 === Object.keys( uniqueViews ).length ) {
		removeCookie( 'nabUniqueViews' );
		return;
	}

	setCookie( 'nabUniqueViews', JSON.stringify( uniqueViews ), {
		expires: 120,
	} );
}

export function canSynchEvent( event: TrackEvent, session: Session ): boolean {
	const heatmap = getHeatmap( event.experiment, session );
	if ( heatmap ) {
		return areHeatmapConditionsValid( heatmap );
	}

	if ( ! doesExperimentHaveValidSegments( event.experiment ) ) {
		return false;
	}

	const experimentsWithPageViews = getExperimentsWithPageViews();
	const timestamp = experimentsWithPageViews[ event.experiment ];
	if ( ! timestamp ) {
		return 'visit' === event.kind;
	}

	if ( 'visit' === event.kind ) {
		if ( 'regular' === event.type ) {
			return true;
		}
		const wait = session.throttle[ event.type ];
		const now = new Date().getTime();
		return timestamp + wait * 60000 < now;
	}

	return (
		'conversion' !== event.kind ||
		canSynchConversion( event.experiment, event.goal, session )
	);
}

export function getExperimentsWithPageViews(): ExpsWithViews {
	try {
		const value = getCookie( 'nabExperimentsWithPageViews' ) ?? '';
		return JSON.parse( value ) as ExpsWithViews;
	} catch ( _ ) {
		return {};
	}
}

// =======
// HELPERS
// =======

let synchedGoals: ReadonlyArray< string > = [];
function canSynchConversion(
	experimentId: ExperimentId,
	goalIndex: GoalIndex,
	session: Session
): boolean {
	if ( ! session.singleConvPerView ) {
		return true;
	}

	if ( synchedGoals.includes( `${ experimentId }-${ goalIndex }` ) ) {
		return false;
	}

	synchedGoals = [ ...synchedGoals, `${ experimentId }-${ goalIndex }` ];
	return true;
}

function setExperimentsWithPageViewsCookie( value: ExpsWithViews ): void {
	if ( 0 === Object.keys( value ).length ) {
		removeCookie( 'nabExperimentsWithPageViews' );
		return;
	}
	setCookie( 'nabExperimentsWithPageViews', JSON.stringify( value ), {
		expires: 120,
	} );
}

let wasCookieCleaned = false;
function removeOldExperimentsFromCookie( session: Session ): void {
	if ( wasCookieCleaned ) {
		return;
	}
	wasCookieCleaned = true;

	const { experiments } = session;
	const experimentsWithPageViews = getExperimentsWithPageViews();
	const experimentIds = experiments.map( ( e ) => e.id );
	const experimentIdsWithPageViews = Object.keys(
		experimentsWithPageViews
	).map( toExperimentId );

	const { global: g, woocommerce: w } = session.throttle;
	const now = new Date().getTime();
	const validationTimestamp = now - ( Math.max( g, w ) + 5 ) * 60000;

	const isInvalid = ( id: ExperimentId ): boolean =>
		! experimentIds.includes( id );
	const wasValidTenDaysAgo = ( id: ExperimentId ): boolean => {
		const timestamp = experimentsWithPageViews[ id ];
		return (
			!! timestamp && Math.abs( now - timestamp ) <= TEN_DAYS_IN_MILLIS
		);
	};

	const newExperimentsWithPageViews = experimentIdsWithPageViews.reduce(
		( result, id ) => {
			const timestamp = experimentsWithPageViews[ id ];

			if ( isInvalid( id ) || ! timestamp ) {
				return !! timestamp && wasValidTenDaysAgo( id )
					? { ...result, [ id ]: timestamp }
					: result;
			}

			return {
				...result,
				[ id ]: Math.max( timestamp, validationTimestamp ),
			};
		},
		{} as ExpsWithViews
	);

	setExperimentsWithPageViewsCookie( newExperimentsWithPageViews );
}

const toExperimentId = ( x: string ) =>
	( Number.parseInt( x ) || 0 ) as ExperimentId;

const getHeatmap = (
	id: ExperimentId,
	session: Session
): Maybe< HeatmapSummary > => session.heatmaps.find( ( hm ) => hm.id === id );
